import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { LAYOUT, SEARCH_PAGE_Z_INDEX } from '@rover/constants/src/search/searchPage.constants';
import MQ from '@rover/kibble/styles/MediaQuery';

import SidebarFiltersCollection from '../Filters/Collections/SidebarFiltersCollectionContainer';

export type Props = {
  onScrollableFilterChange: () => void;
};

const SIDEBAR_TOP_MARGIN = 6;
const DEFAULT_SIDEBAR_HEIGHT = 1358;

const Wrapper = styled.div<{ minHeight: number }>`
  align-self: stretch;
  flex: 0 0 ${LAYOUT.LEFT_WIDTH}px;
  margin-left: ${LAYOUT.GUTTER}px;
  margin-right: ${LAYOUT.GUTTER}px;
  position: relative;
  z-index: ${SEARCH_PAGE_Z_INDEX.STICKY_SIDEBAR};
  min-height: ${({ minHeight }) => minHeight}px;
  display: none;

  ${MQ.SM_UP.toString()} {
    display: block;
  }
`;

const Sticky = styled.div<{ innerHeight: number; windowHeight: number }>`
  position: sticky;
  background-color: ${({ theme }) => theme.background.primary};
  width: ${LAYOUT.LEFT_WIDTH}px;
  border-radius: ${({ theme }) => `${theme.radii.secondary} ${theme.radii.secondary} 0px 0px`};

  top: ${({ innerHeight, windowHeight }) => {
    const sidebarHeight = innerHeight || DEFAULT_SIDEBAR_HEIGHT;
    const sidebarAndMarginHeight = sidebarHeight + SIDEBAR_TOP_MARGIN;
    const topOffset =
      windowHeight > sidebarAndMarginHeight
        ? `${SIDEBAR_TOP_MARGIN}px`
        : `calc(100% - ${sidebarHeight}px)`;
    return topOffset;
  }};
`;

const StickySidebar: React.FC<Props> = ({ onScrollableFilterChange }) => {
  const [innerHeight, setInnerHeight] = useState(0);
  const [windowHeight, setWindowHeight] = useState(9999);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);

  const updateInnerHeight = useCallback(() => {
    setTimeout(() => {
      if (stickyRef?.current?.offsetHeight && stickyRef.current.offsetHeight !== innerHeight) {
        setInnerHeight(stickyRef.current?.offsetHeight || 0);
      }
    }, 200);
  }, [innerHeight]);

  const updateWindowHeight = useCallback(() => {
    setWindowHeight(window.innerHeight); // eslint-disable-line rover/no-platform-specific-globals-or-imports
  }, []);

  useEffect(() => {
    updateInnerHeight();
    updateWindowHeight();

    if (process.env.JS_ENV_CLIENT) {
      window.addEventListener('resize', updateWindowHeight);
    }
    return () => {
      if (process.env.JS_ENV_CLIENT) {
        window.removeEventListener('resize', updateWindowHeight);
      }
    };
  }, [updateInnerHeight, updateWindowHeight]);

  return (
    <Wrapper ref={wrapperRef} minHeight={innerHeight}>
      <Sticky ref={stickyRef} innerHeight={innerHeight} windowHeight={windowHeight}>
        <SidebarFiltersCollection
          onScrollableFilterChange={onScrollableFilterChange}
          onUpdateInnerHeight={updateInnerHeight}
        />
      </Sticky>
    </Wrapper>
  );
};

export default React.memo(StickySidebar);
